"use client";

import { useTranslations } from "next-intl";
import { useRef } from "react";
import {
  SIGN_IN_CODE_LENGTH,
  SignInForm,
} from "@/components/sign-in/SignInForm";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { AppStoreBadge } from "./AppStoreBadge";
import { AppStoreBanner } from "./AppStoreBanner";
import { AdRevealBackground } from "./adReveal/AdRevealBackground";
import { CreatorAuthFrame } from "./CreatorAuthFrame";

/**
 * NewForm One's sign-in: the shared form, in the creator frame and copy.
 *
 * Google returns to the page that started it (`SignInForm` sends the current
 * URL as the callback), so a caller that needs to resume a step after the
 * round trip keeps that step in its own query string.
 */
export function CreatorSignIn({
  initialEmail = "",
  legacyLink,
  googleEnabled,
  lede,
  onSignedIn,
}: {
  initialEmail?: string;
  legacyLink: boolean;
  googleEnabled: boolean;
  /** Replaces the returning creator's line, for a page that says why to sign in. */
  lede?: string;
  onSignedIn: () => Promise<void>;
}) {
  const t = useTranslations("signIn");
  /** The block the ad wall dims behind, so the copy stays readable. */
  const guardRef = useRef<HTMLDivElement>(null);

  return (
    <CreatorAuthFrame
      background={<AdRevealBackground guardRef={guardRef} />}
      banner={<AppStoreBanner />}
    >
      {/*
        The guard goes on the block that hugs the content, not on the column
        that centres it. That column is `flex-1`, so it is as tall as the
        page — and the ad wall sizes both its readability damping and its
        idle orbit from this rect, so measuring the stretched parent put the
        orbit's radius past the top and bottom of the screen and dimmed most
        of it.
      */}
      <SignInForm
        app="creator"
        idPrefix="creator"
        contentRef={guardRef}
        initialEmail={initialEmail}
        googleEnabled={googleEnabled}
        onSignedIn={onSignedIn}
        footer={
          // Under the form, not in it: the app is the other way in, and a
          // badge inside the column of fields would read as a third button.
          // Large, because on a phone it is the better way in and the one
          // thing on the screen that is not text.
          <div className="flex justify-center pt-nf-ds-8">
            <AppStoreBadge />
          </div>
        }
        notice={
          legacyLink && (
            <Alert>
              <AlertDescription>{t("legacyLink")}</AlertDescription>
            </Alert>
          )
        }
        copy={{
          // Brand, not metadata — the one place on this screen where colour
          // is the point rather than decoration.
          headline: t.rich("headline", {
            highlight: (chunks) => (
              <span className="bg-[image:var(--nf-ds-brand-grad)] bg-clip-text text-transparent">
                {chunks}
              </span>
            ),
          }),
          lede: lede ?? t("lede"),
          checkEmail: t("checkEmail"),
          codeSent: (email) =>
            t.rich("codeSent", {
              length: SIGN_IN_CODE_LENGTH,
              email,
              emphasis: (chunks) => (
                <span className="text-nf-ds-fg">{chunks}</span>
              ),
            }),
          emailLabel: t("emailLabel"),
          emailPlaceholder: t("emailPlaceholder"),
          emailSubmit: t("emailSubmit"),
          google: t("google"),
          codeHint: t("codeHint", { length: SIGN_IN_CODE_LENGTH }),
          codeLabel: t("codeLabel"),
          signingIn: t("signingIn"),
          resendCountdown: (seconds) => t("resendCountdown", { seconds }),
          resend: t("resend"),
          differentEmail: t("differentEmail"),
          errors: {
            googleFinish: t("errors.googleFinish"),
            googleStart: t("errors.googleStart"),
            sendFailed: t("errors.sendFailed"),
            codeRejected: t("errors.codeRejected"),
            verifyFailed: t("errors.verifyFailed"),
            offline: t("errors.offline"),
          },
        }}
      />
    </CreatorAuthFrame>
  );
}
